import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useCreateForm } from '@wordpuppi/shared';
import type { CreateFormData } from '@wordpuppi/shared';
import { FormBuilder } from '@/components/FormBuilder';
import { FormTemplatePicker } from '@/components/FormTemplatePicker';
import { toast } from 'sonner';

export const Route = createFileRoute('/sites/$siteSlug/forms/new')({
  component: NewFormPage,
});

function NewFormPage() {
  const { siteSlug } = Route.useParams();
  const navigate = useNavigate();
  const createForm = useCreateForm(siteSlug);

  // Pick a template first (or Blank). `template` may be undefined (blank form).
  const [started, setStarted] = useState(false);
  const [template, setTemplate] = useState<CreateFormData | undefined>(undefined);

  const handleSubmit = async (data: CreateFormData) => {
    try {
      const result = await createForm.mutateAsync(data);
      if (result?.data?.id) {
        toast.success('Form created');
        navigate({
          to: '/sites/$siteSlug/forms/$formId',
          params: { siteSlug, formId: result.data.id },
        });
      } else {
        toast.success('Form created');
        navigate({ to: '/sites/$siteSlug/forms', params: { siteSlug } });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create form');
    }
  };

  if (!started) {
    return (
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">New Form</h1>
          <p className="text-muted-foreground mt-1">
            Pick a starting point — you can change everything after.
          </p>
        </div>
        <FormTemplatePicker
          onSelect={(data) => {
            setTemplate(data);
            setStarted(true);
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">New Form</h1>
        <p className="text-muted-foreground mt-1">
          Create a new form for your site
        </p>
      </div>

      <FormBuilder
        initialData={template}
        onSubmit={handleSubmit}
        isSubmitting={createForm.isPending}
        submitLabel="Create Form"
      />
    </div>
  );
}
